import "./styles.scss";

import CardCreateRecipientGiftCard from "components/card-send-create/card-create-recipient-gift-card";
import CardThumbnail from "components/cards/card-thumbnail";
import CardSendControlCenterActions from "components/ui/card-send-control-center-dialog/card-send-control-center-actions";
import CardSendControlCenterHeader from "components/ui/card-send-control-center-dialog/card-send-control-center-header";
import CardSendControlCenterList from "components/ui/card-send-control-center-dialog/card-send-control-center-list";
import CardSendControlCenterListItem from "components/ui/card-send-control-center-dialog/card-send-control-center-list-item";
import { BaIconLightbulb } from "components/ui/icons";
import ShareOptions from "components/ui/share-options";
import { getCardSendControlCenterProps } from "components/utils/cards";
import { supportNativeShare } from "components/utils/device";
import { useQueryDict } from "hooks/app";
import PropTypes from "prop-types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";

const CardSendControlCenter = ({ cardSend, location, queryParams, onDelete, withNativeShare, onSignersClick, onRecipientSentByLink, onClose }) => {
  const navigate = useNavigate();
  const { updateSearch } = useQueryDict();
  const { cardSendId, card, signers, recipients, sentDttm, magicLinkFull, senderName, message, cardName, draftInd, giftCards, deliverDt, reminderName } =
    cardSend;

  const validSigners = signers?.filter((s) => !s.isSender && (s.inviteEmail || s.signedDttm)) || [];
  const minutesRemaining = deliverDt ? (new Date(`${deliverDt}.000Z`) - new Date()) / 60000 : -1;
  const isImmediateSending = location === "card-create-success" && minutesRemaining < 5;

  const useNativeShare = typeof withNativeShare === "undefined" ? supportNativeShare : withNativeShare;

  const {
    title,
    subtitle,
    sendTextMessageButtonTitle,
    nativeShareOptionsInfo,
    shareNowButtonTitle,
    shareOptionsInfo,
    recipientsToggled,
    withCard,
    withDate,
    withShareNowButton,
    withCardOptions,
    withCardDetails,
  } = getCardSendControlCenterProps(location, cardSend, isImmediateSending, reminderName);

  const getSendByLinkDialogTitle = (recipientName) => {
    const singleRecipient = recipients?.length === 1 ? recipients[0].recipientName : "";
    return `Share your card${recipientName || singleRecipient ? ` with ${recipientName || singleRecipient}` : ""}`;
  };

  const onMyCardsClick = (e) => {
    e.preventDefault();
    onClose();
    let type = "upcoming";
    if (sentDttm) {
      type = "sent";
    } else if (draftInd === 1) {
      type = "drafts";
    }
    navigate(`/mycards/${type}${updateSearch({ card_send_id: cardSendId })}`);
  };

  const shareOptionsProps = {
    url: magicLinkFull,
    smsLink: `sms://?body=${senderName}%20has%20sent%20you%20a%20card%0A${""}%0AView%20card:%20${encodeURIComponent(magicLinkFull)}`,
    dialogTitle: getSendByLinkDialogTitle(),
    buttonTitle: shareNowButtonTitle,
    textButtonTitle: sendTextMessageButtonTitle,
    useNativeShare: useNativeShare,
    copyLinkButtonTitle: "Copy Card Link",
    title: `${senderName} has sent you a card`,
    text: message || cardName,
  };

  return (
    <div className="card-send-control-center-container">
      <CardSendControlCenterHeader cardSend={cardSend} title={title} subtitle={subtitle} withDate={withDate} />
      {withCard && card && (
        <div style={{ width: "300px", margin: "0 auto" }}>
          <CardThumbnail className="thumbnail-image" card={card} clean sizes="300px" />
        </div>
      )}
      {withShareNowButton && (
        <div className="card-send-control-center-link-option">
          <ShareOptions {...shareOptionsProps} />
          <div className="card-send-control-center-link-option-info">{useNativeShare ? nativeShareOptionsInfo : shareOptionsInfo}</div>
        </div>
      )}
      {withCardOptions && (
        <CardSendControlCenterActions
          cardSend={cardSend}
          queryParams={queryParams}
          shareOptionsProps={shareOptionsProps}
          onSignersClick={onSignersClick}
          onClose={onClose}
          onDelete={onDelete}
        />
      )}
      {withCardDetails && (
        <CardSendControlCenterList listTitle={`RECIPIENTS ${recipients?.length > 0 ? `(${recipients.length})` : ""}`} initialOpen={!!recipientsToggled}>
          {recipients?.length === 0 ? (
            <CardSendControlCenterListItem subtitle="delivery via link" />
          ) : (
            recipients.map((r) => (
              <CardSendControlCenterListItem
                key={`card-send-recipient-${r.cardSendRecipientId}`}
                title={r.recipientName}
                subtitle={r.recipientEmail}
                status={isImmediateSending && r.recipientManualId !== 1 ? "SENT" : draftInd === 1 ? "" : r.status?.toUpperCase()}
                action={
                  r.recipientManualId === 1 || !r.recipientEmail ? (
                    <ShareOptions {...shareOptionsProps} onSuccess={() => onRecipientSentByLink(r)}>
                      <span style={{ color: "#17a17e", cursor: "pointer" }}>{r.sentDttm ? "SHARE AGAIN" : "SHARE CARD"}</span>
                    </ShareOptions>
                  ) : null
                }
              >
                {giftCards
                  ?.filter((g) => g.cardSendRecipientId === r.cardSendRecipientId)
                  ?.map((g, i) => (
                    <div key={`gift-card-${g.cardSendRecipientId}-${i}`} style={{ padding: "0 1rem 1rem" }}>
                      <CardCreateRecipientGiftCard cardSend={cardSend} cardSendRecipient={r} giftCard={g} />
                    </div>
                  ))}
              </CardSendControlCenterListItem>
            ))
          )}
        </CardSendControlCenterList>
      )}
      {withCardDetails && validSigners.length > 0 && (
        <CardSendControlCenterList listTitle={`SIGNERS (${signers.length})`}>
          {validSigners.map((s) => (
            <CardSendControlCenterListItem
              key={`card-send-signer-${s.cardSignerId}`}
              title={s.inviteName || s.signatureTx || " "}
              subtitle={s.inviteEmail || s.receiveCardEmail || " "}
              status={s.signedDttm ? "SIGNED" : draftInd === 1 ? "PENDING" : "INVITED"}
            />
          ))}
        </CardSendControlCenterList>
      )}
      <div className="card-send-control-center-footer-my-cards">
        <BaIconLightbulb className="card-send-control-center-footer-icon" />
        <p className="card-send-control-center-footer-text">
          Manage this card at any time in <Link onClick={onMyCardsClick}>My Cards.</Link>
        </p>
      </div>
      {/* {footerVersion === 'card-link' &&
        <div className="card-send-control-center-footer-card-link">
          <p className="section-info">FINAL CARD LINK</p>
          <h4 className="section-title">{sentDttm ? 'Send the card by link anytime' : 'Send the card by link anytime'}</h4>
          <p className="section-subtitle">
            <span>Copy the link and send it yourself on your favorite chat app or social media platform. </span>
            {!sentDttm && isGroupCard &&
              <span>Allow enough time for your signers, though!</span>
            }
          </p>
          {supportNativeShare ?
            <ShareButton
              url={magicLinkFull}
              dialogTitle="Send by Link"
              buttonTitle="Copy Card Link"
              title={`${senderName} has sent you a card`}
              text={message || cardName}
              onError={() => showAlert({ message: 'There was an error trying to share', type: 'error' })}
            >
              <Button onClick={() => {}}>SHARE NOW</Button>
            </ShareButton>
          :
            <MagicLink
              link={magicLinkFull}
              buttonProps={{
                sx: { height: 35, padding: '0.7rem 1rem 0.7rem 0.7rem', marginLeft: '-3px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 30, borderBottomRightRadius: 30, fontSize: '0.7rem' }
              }}
              buttonTitle="Copy"
              // marketingId={magicLinkMarketingId}
              className="magic-link"
            />
          }
        </div>
      } */}
    </div>
  );
};

CardSendControlCenter.propTypes = {
  cardSend: PropTypes.object.isRequired,
  location: PropTypes.string.isRequired,
  queryParams: PropTypes.object,
  withNativeShare: PropTypes.bool,
  isImmediateSending: PropTypes.bool,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSignersClick: PropTypes.func.isRequired,
  onRecipientSentByLink: PropTypes.func.isRequired,
};

CardSendControlCenter.defaultProps = {
  queryParams: {},
  onDelete: undefined,
};

export default CardSendControlCenter;
